import React, { useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  Sector,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShip,
  faTruck,
  faPlane,
  faQuestion,
  faInfoCircle,
  faDownload,
  faExpand,
} from "@fortawesome/free-solid-svg-icons";

const TransportTypeDistributionChart = ({ data, onChartClick }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const COLORS = {
    Sea: "#3b82f6", // blue-500
    Air: "#8b5cf6", // purple-500
    Road: "#10b981", // emerald-500
    Unknown: "#9ca3af", // gray-400
  };

  const getTransportIcon = (type) => {
    switch (type) {
      case "Sea":
        return faShip;
      case "Road":
        return faTruck;
      case "Air":
        return faPlane;
      default:
        return faQuestion;
    }
  };

  // Calculate total for percentage display
  const total = data.reduce((sum, entry) => sum + entry.value, 0);

  // Handle mouse enter for active segment
  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  // Handle mouse leave
  const onPieLeave = () => {
    setActiveIndex(null);
  };

  // Render active shape with more details
  const renderActiveShape = (props) => {
    const {
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      value,
    } = props;

    const percent = Math.round((value / total) * 100);

    return (
      <g>
        <text
          x={cx}
          y={cy}
          dy={-20}
          textAnchor="middle"
          fill="#333"
          fontSize={14}
          fontWeight="bold"
        >
          {payload.name}
        </text>
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          fill="#333"
          fontSize={16}
          fontWeight="bold"
        >
          {value}
        </text>
        <text
          x={cx}
          y={cy}
          dy={20}
          textAnchor="middle"
          fill="#666"
          fontSize={12}
        >
          {percent}% of total
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 6}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
      </g>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0];
      const percentage = Math.round((item.value / total) * 100);

      return (
        <div className="bg-white p-3 shadow-md border border-gray-200 rounded-md">
          <div className="flex items-center mb-1">
            <FontAwesomeIcon
              icon={getTransportIcon(item.name)}
              className="mr-2"
              style={{ color: item.color }}
            />
            <span className="font-medium">{item.name}</span>
          </div>
          <div className="text-gray-700 text-sm">
            <div>
              Shipments: <span className="font-medium">{item.value}</span>
            </div>
            <div>
              Percentage: <span className="font-medium">{percentage}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom legend items with icons
  const renderLegend = (props) => {
    const { payload } = props;

    return (
      <ul className="flex flex-wrap justify-center gap-4 mb-4">
        {payload.map((entry, index) => (
          <li
            key={`item-${index}`}
            className="flex items-center text-sm cursor-pointer hover:opacity-80"
            onClick={() => handleLegendClick(entry)}
          >
            <FontAwesomeIcon
              icon={getTransportIcon(entry.value)}
              className="mr-1"
              style={{ color: entry.color }}
            />
            <span className="mr-1">{entry.value}:</span>
            <span className="font-medium">{entry.payload.value}</span>
          </li>
        ))}
      </ul>
    );
  };

  // Handle legend click
  const handleLegendClick = (entry) => {
    if (typeof onChartClick === "function") {
      onChartClick(entry);
    }
  };

  // No data display
  if (!data || data.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-500">
        <FontAwesomeIcon icon={faShip} className="text-3xl mb-2 opacity-50" />
        <div>No transport data available</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ResponsiveContainer width="100%" height="85%">
        <PieChart>
          <Pie
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            paddingAngle={2}
            dataKey="value"
            onMouseEnter={onPieEnter}
            onMouseLeave={onPieLeave}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[entry.name] || COLORS.Unknown}
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend content={renderLegend} verticalAlign="bottom" height={36} />
        </PieChart>
      </ResponsiveContainer>

      {/* Footer */}
      <div className="mt-auto pt-2 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
        <div className="flex items-center">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          <span>Hover over sections for details</span>
        </div>
        <div className="flex space-x-1">
          <button
            title="Download chart"
            className="p-1 hover:text-blue-600 transition-colors"
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            title="Expand chart"
            className="p-1 hover:text-blue-600 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
        </div>
      </div>

      {/* Expanded View Modal */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsExpanded(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-4/5 h-4/5 max-w-6xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <FontAwesomeIcon icon={faShip} className="text-blue-600 mr-2" />
                Transport Type Distribution
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsExpanded(false)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="h-5/6">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    activeIndex={activeIndex}
                    activeShape={renderActiveShape}
                    data={data}
                    cx="50%"
                    cy="50%"
                    innerRadius={100}
                    outerRadius={140}
                    paddingAngle={2}
                    dataKey="value"
                    onMouseEnter={onPieEnter}
                    onMouseLeave={onPieLeave}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[entry.name] || COLORS.Unknown}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    content={renderLegend}
                    verticalAlign="bottom"
                    height={36}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportTypeDistributionChart;
